import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import {
  ArrowDown,
  ArrowUp,
  CornerUpLeft,
  CornerUpRight,
  Minus,
  MoveUpLeft,
  MoveUpRight,
  RectangleHorizontal,
} from 'lucide-preact';
import type { CoasterRuntime, RuntimeSnapshot } from '../app/runtime-types.ts';
import type { AuthoredBank, AuthoredPitch } from '../builder-v2/index.ts';
import {
  BASIC_BANK_CHOICES,
  BASIC_DIRECTION_CHOICES,
  BASIC_SLOPE_CHOICES,
  SPECIAL_TRACK_GROUPS,
  basicChoiceAvailable,
  basicDisplayLabel,
  basicSelectionForDescriptor,
  contextualSpecialSuccessor,
  contextualSpecialTrackEntries,
  defaultBasicSelection,
  isSpecialTrackDescriptor,
  resolveBasicRegularPiece,
  selectionWithChoice,
  specialTrackGroup,
  type BasicBankId,
  type BasicDirectionId,
  type BasicRegularAxis,
  type BasicRegularChoice,
  type BasicSlopeId,
} from './basic-build-catalog.ts';
import { AuthoredPieceThumbnail } from './AuthoredPieceThumbnail.tsx';

interface BasicBuildProps {
  readonly state: RuntimeSnapshot;
  readonly runtime: CoasterRuntime | null;
}

const TRACK_TYPE_CHOICES = [
  ['normal', 'Normal'],
  ['lift', 'Lift'],
  ['launch', 'Launch'],
  ['brake', 'Brakes'],
  ['photo', 'Photo'],
] as const;

function currentPitch(state: RuntimeSnapshot): AuthoredPitch {
  return state.buildEnd?.authoredPose?.pitchDegrees ?? 0;
}

function currentBank(state: RuntimeSnapshot): AuthoredBank {
  if (state.buildEnd?.authoredPose) return state.buildEnd.authoredPose.bank;
  const bank = state.buildEnd?.pose.bankDegrees ?? 0;
  return bank < 0 ? 'left' : bank > 0 ? 'right' : 'level';
}

function useSelectedOptionScroller(selectedKey: string) {
  const scrollerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const selected = scroller?.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!scroller || !selected) return;

    const frame = window.requestAnimationFrame(() => {
      selected.scrollIntoView({
        block: 'nearest',
        inline: 'center',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [selectedKey]);

  return scrollerRef;
}

function TrackTypeControl({
  state,
  runtime,
  mobile = false,
}: BasicBuildProps & { mobile?: boolean }) {
  const selected = state.authoredCatalog.find(
    (entry) => entry.id === state.authoredSelection.descriptorId,
  );
  return (
    <div
      class={mobile ? 'mobile-track-type-control' : 'segmented-control track-type-control'}
      role="group"
      aria-label="Track Type"
    >
      {TRACK_TYPE_CHOICES.map(([value, label]) => {
        const allowed = selected?.allowedTrackTypes.includes(value) ?? false;
        return (
          <button
            key={value}
            type="button"
            aria-pressed={state.authoredSelection.trackType === value}
            disabled={!allowed}
            title={
              allowed ? `${label} track` : `${selected?.label ?? 'This piece'} cannot use ${label}`
            }
            onClick={() => runtime?.setAuthoredTrackType(value)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function DirectionGlyph({ id }: { readonly id: BasicDirectionId }) {
  const props = { 'aria-hidden': true, size: 25, strokeWidth: 2.2 } as const;
  if (id === 'direction.left.small') return <CornerUpLeft {...props} />;
  if (id === 'direction.left.large') return <CornerUpLeft {...props} class="is-large-turn" />;
  if (id === 'direction.left.45') return <MoveUpLeft {...props} />;
  if (id === 'direction.right.45') return <MoveUpRight {...props} />;
  if (id === 'direction.right.large') return <CornerUpRight {...props} class="is-large-turn" />;
  if (id === 'direction.right.small') return <CornerUpRight {...props} />;
  return <ArrowUp {...props} />;
}

function SlopeGlyph({ id }: { readonly id: BasicSlopeId }) {
  if (id === 'slope.n90') return <ArrowDown aria-hidden="true" size={25} strokeWidth={2.2} />;
  if (id === 'slope.90') return <ArrowUp aria-hidden="true" size={25} strokeWidth={2.2} />;
  return (
    <span class={`basic-slope-glyph ${id.replace('.', '-')}`} aria-hidden="true">
      <Minus size={26} strokeWidth={2.4} />
      <Minus size={27} strokeWidth={1.4} />
    </span>
  );
}

function BankGlyph({ id }: { readonly id: BasicBankId }) {
  return (
    <span class={`basic-bank-glyph ${id.replace('.', '-')}`} aria-hidden="true">
      <RectangleHorizontal size={25} strokeWidth={2.1} />
      <Minus size={28} strokeWidth={1.8} />
    </span>
  );
}

function SelectedPieceCard({ state, runtime }: BasicBuildProps) {
  const selected = state.authoredCatalog.find(
    (entry) => entry.id === state.authoredSelection.descriptorId,
  );
  const placeReady = state.preview?.kind === 'piece' && state.preview.canAccept;
  return (
    <section class="basic-selected-result" aria-label="Selected official piece">
      <h4>Selected Piece</h4>
      <div class="basic-selected-piece-preview">
        {selected && <AuthoredPieceThumbnail descriptorId={selected.id} />}
      </div>
      <b>{selected ? basicDisplayLabel(selected.id, selected.label) : 'Choose a piece'}</b>
      <button
        class="primary-action"
        type="button"
        disabled={!placeReady}
        onClick={() => runtime?.acceptPreview()}
      >
        Place Piece
      </button>
    </section>
  );
}

function RegularBuildOptions({ state, runtime }: BasicBuildProps) {
  const pitch = currentPitch(state);
  const bank = currentBank(state);
  const selectedId = state.authoredSelection.descriptorId;
  const selection =
    basicSelectionForDescriptor(selectedId, pitch, bank) ?? defaultBasicSelection(pitch, bank);
  const resolvedSelection = resolveBasicRegularPiece(state.authoredCatalog, selection, pitch, bank);

  useEffect(() => {
    if (resolvedSelection && resolvedSelection.id !== selectedId) {
      runtime?.selectAuthoredPiece(resolvedSelection.id, false);
    }
  }, [resolvedSelection?.id, runtime, selectedId]);

  const choose = (axis: BasicRegularAxis, choice: BasicRegularChoice): void => {
    const target = resolveBasicRegularPiece(
      state.authoredCatalog,
      selectionWithChoice(selection, axis, choice),
      pitch,
      bank,
    );
    if (target) runtime?.selectAuthoredPiece(target.id, false);
  };
  const available = (axis: BasicRegularAxis, choice: BasicRegularChoice): boolean =>
    basicChoiceAvailable(state.authoredCatalog, selection, axis, choice, pitch, bank);
  const recommended = (axis: BasicRegularAxis, choice: BasicRegularChoice): boolean =>
    resolveBasicRegularPiece(
      state.authoredCatalog,
      selectionWithChoice(selection, axis, choice),
      pitch,
      bank,
    )?.recommendedRank === 1;

  return (
    <div class="basic-options-panel">
      <section class="basic-option-row basic-direction-row">
        <h4>Direction</h4>
        <div role="group" aria-label="Direction">
          {BASIC_DIRECTION_CHOICES.map((choice) => (
            <button
              key={choice.id}
              type="button"
              aria-label={choice.accessibleLabel}
              aria-pressed={selection.direction === choice.id}
              disabled={!available('direction', choice.id)}
              class={recommended('direction', choice.id) ? 'is-route-recommended' : ''}
              onClick={() => choose('direction', choice.id)}
            >
              <DirectionGlyph id={choice.id} />
              <span>{choice.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section class="basic-option-row basic-slope-row">
        <h4>Slope</h4>
        <div role="group" aria-label="Slope">
          {BASIC_SLOPE_CHOICES.map((choice) => (
            <button
              key={choice.id}
              type="button"
              aria-label={`${choice.pitch < 0 ? 'Down ' : choice.pitch > 0 ? 'Up ' : ''}${choice.label}`}
              aria-pressed={selection.slope === choice.id}
              disabled={!available('slope', choice.id)}
              class={recommended('slope', choice.id) ? 'is-route-recommended' : ''}
              onClick={() => choose('slope', choice.id)}
            >
              <SlopeGlyph id={choice.id} />
              <span>{choice.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section class="basic-option-row basic-bank-row">
        <h4>Banking</h4>
        <div role="group" aria-label="Banking">
          {BASIC_BANK_CHOICES.map((choice) => (
            <button
              key={choice.id}
              type="button"
              aria-pressed={selection.bank === choice.id}
              disabled={!available('bank', choice.id)}
              class={recommended('bank', choice.id) ? 'is-route-recommended' : ''}
              onClick={() => choose('bank', choice.id)}
            >
              <BankGlyph id={choice.id} />
              <span>{choice.label}</span>
            </button>
          ))}
        </div>
      </section>

      <section class="basic-option-row basic-track-type-row">
        <h4>Track Type</h4>
        <TrackTypeControl state={state} runtime={runtime} />
      </section>
    </div>
  );
}

export function BasicBuildComposer({ state, runtime }: BasicBuildProps) {
  const selectedId = state.authoredSelection.descriptorId;
  const [catalogMode, setCatalogMode] = useState<'regular' | 'special'>(
    isSpecialTrackDescriptor(selectedId) ? 'special' : 'regular',
  );
  const specialEntries = state.authoredCatalog.filter((entry) =>
    isSpecialTrackDescriptor(entry.id),
  );
  const contextualSpecials = contextualSpecialTrackEntries(specialEntries);
  const regularAvailableCount = state.authoredCatalog.filter(
    (entry) => entry.allowed && !isSpecialTrackDescriptor(entry.id) && entry.category !== 'station',
  ).length;
  const specialAvailableCount = contextualSpecials.filter(({ entry }) => entry.allowed).length;

  useEffect(() => {
    if (isSpecialTrackDescriptor(selectedId)) {
      setCatalogMode('special');
    }
  }, [selectedId]);

  useEffect(() => {
    if (!isSpecialTrackDescriptor(selectedId)) return;
    const successor = contextualSpecialSuccessor(specialEntries, selectedId);
    if (successor && successor.id !== selectedId) {
      runtime?.selectAuthoredPiece(successor.id, false);
    }
  }, [runtime, selectedId, specialEntries]);

  return (
    <div class="basic-build-composer" data-testid="basic-build-composer">
      <div class="basic-catalog-mode" role="group" aria-label="Track piece catalog">
        <button
          type="button"
          aria-pressed={catalogMode === 'regular'}
          onClick={() => setCatalogMode('regular')}
        >
          Regular Track <span>{regularAvailableCount}</span>
        </button>
        <button
          type="button"
          aria-pressed={catalogMode === 'special'}
          onClick={() => setCatalogMode('special')}
        >
          Special Track <span>{specialAvailableCount}</span>
        </button>
      </div>

      {catalogMode === 'regular' ? (
        <div class="basic-intent-browser" data-testid="basic-intent-browser">
          <RegularBuildOptions state={state} runtime={runtime} />
          <SelectedPieceCard state={state} runtime={runtime} />
        </div>
      ) : (
        <div class="basic-special-layout">
          <div class="basic-special-browser" data-testid="basic-special-browser">
            {SPECIAL_TRACK_GROUPS.map((group) => {
              const entries = contextualSpecials.filter(
                ({ entry }) => specialTrackGroup(entry.id) === group.id,
              );
              if (!entries.length) return null;
              return (
                <section key={group.id}>
                  <h4>{group.label}</h4>
                  <div class="basic-special-grid">
                    {entries.map(({ entry, displayLabel }) => (
                      <button
                        key={entry.id}
                        type="button"
                        class={`${entry.selected ? 'is-previewing' : ''} ${
                          entry.recommendedRank === 1 ? 'is-route-recommended' : ''
                        }`}
                        aria-pressed={entry.selected}
                        disabled={!entry.allowed}
                        title={
                          entry.allowed ? entry.description : (entry.reason ?? 'Unavailable here')
                        }
                        onClick={() => runtime?.selectAuthoredPiece(entry.id, false)}
                      >
                        <AuthoredPieceThumbnail descriptorId={entry.id} />
                        <b>{displayLabel}</b>
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
          <SelectedPieceCard state={state} runtime={runtime} />
        </div>
      )}
    </div>
  );
}

export function MobileBasicPieceBrowser({
  state,
  runtime,
  catalogMode,
}: BasicBuildProps & { readonly catalogMode: 'regular' | 'special' }) {
  const selectedId = state.authoredSelection.descriptorId;
  const pitch = currentPitch(state);
  const bank = currentBank(state);
  const selection =
    basicSelectionForDescriptor(selectedId, pitch, bank) ?? defaultBasicSelection(pitch, bank);
  const specialEntries = SPECIAL_TRACK_GROUPS.flatMap((group) =>
    state.authoredCatalog.filter((entry) => specialTrackGroup(entry.id) === group.id),
  );
  const specials = contextualSpecialTrackEntries(specialEntries);
  const directionScrollerRef = useSelectedOptionScroller(selection.direction);
  const slopeScrollerRef = useSelectedOptionScroller(selection.slope);
  const bankScrollerRef = useSelectedOptionScroller(selection.bank);
  const trackTypeScrollerRef = useSelectedOptionScroller(state.authoredSelection.trackType);
  const specialScrollerRef = useSelectedOptionScroller(selectedId);

  useEffect(() => {
    if (!isSpecialTrackDescriptor(selectedId)) return;
    const successor = contextualSpecialSuccessor(specialEntries, selectedId);
    if (successor && successor.id !== selectedId) {
      runtime?.selectAuthoredPiece(successor.id, false);
    }
  }, [runtime, selectedId, specialEntries]);

  const choose = (axis: BasicRegularAxis, choice: BasicRegularChoice): void => {
    const target = resolveBasicRegularPiece(
      state.authoredCatalog,
      selectionWithChoice(selection, axis, choice),
      pitch,
      bank,
    );
    if (target) runtime?.selectAuthoredPiece(target.id, false);
  };
  const available = (axis: BasicRegularAxis, choice: BasicRegularChoice): boolean =>
    basicChoiceAvailable(state.authoredCatalog, selection, axis, choice, pitch, bank);
  const recommended = (axis: BasicRegularAxis, choice: BasicRegularChoice): boolean =>
    resolveBasicRegularPiece(
      state.authoredCatalog,
      selectionWithChoice(selection, axis, choice),
      pitch,
      bank,
    )?.recommendedRank === 1;

  return (
    <div class="mobile-piece-browser">
      {catalogMode === 'regular' ? (
        <div class="mobile-regular-options" data-testid="mobile-regular-options">
          <section class="mobile-option-row mobile-direction-row">
            <h4>Direction</h4>
            <div
              ref={directionScrollerRef}
              class="mobile-option-scroller"
              role="group"
              aria-label="Direction"
            >
              {BASIC_DIRECTION_CHOICES.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  aria-label={choice.accessibleLabel}
                  aria-pressed={selection.direction === choice.id}
                  disabled={!available('direction', choice.id)}
                  class={recommended('direction', choice.id) ? 'is-route-recommended' : ''}
                  onClick={() => choose('direction', choice.id)}
                >
                  <DirectionGlyph id={choice.id} />
                  <span>{choice.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section class="mobile-option-row mobile-slope-row">
            <h4>Slope</h4>
            <div
              ref={slopeScrollerRef}
              class="mobile-option-scroller"
              role="group"
              aria-label="Slope"
            >
              {BASIC_SLOPE_CHOICES.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  aria-label={`${choice.pitch < 0 ? 'Down ' : choice.pitch > 0 ? 'Up ' : ''}${choice.label}`}
                  aria-pressed={selection.slope === choice.id}
                  disabled={!available('slope', choice.id)}
                  class={recommended('slope', choice.id) ? 'is-route-recommended' : ''}
                  onClick={() => choose('slope', choice.id)}
                >
                  <SlopeGlyph id={choice.id} />
                  <span>{choice.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section class="mobile-option-row mobile-bank-row">
            <h4>Banking</h4>
            <div
              ref={bankScrollerRef}
              class="mobile-option-scroller"
              role="group"
              aria-label="Banking"
            >
              {BASIC_BANK_CHOICES.map((choice) => (
                <button
                  key={choice.id}
                  type="button"
                  aria-pressed={selection.bank === choice.id}
                  disabled={!available('bank', choice.id)}
                  class={recommended('bank', choice.id) ? 'is-route-recommended' : ''}
                  onClick={() => choose('bank', choice.id)}
                >
                  <BankGlyph id={choice.id} />
                  <span>{choice.label}</span>
                </button>
              ))}
            </div>
          </section>

          <section class="mobile-option-row mobile-track-type-row">
            <h4>Track Type</h4>
            <div ref={trackTypeScrollerRef} class="mobile-option-scroller">
              <TrackTypeControl state={state} runtime={runtime} mobile />
            </div>
          </section>
        </div>
      ) : (
        <div
          ref={specialScrollerRef}
          class="mobile-special-browser"
          data-testid="mobile-special-browser"
          aria-label="Special Track choices"
        >
          {specials.map(({ entry, displayLabel }) => (
            <button
              key={entry.id}
              type="button"
              class={`mobile-special-piece ${entry.selected ? 'is-previewing' : ''} ${
                entry.recommendedRank === 1 ? 'is-route-recommended' : ''
              }`}
              aria-pressed={entry.selected}
              disabled={!entry.allowed}
              title={entry.allowed ? entry.description : (entry.reason ?? 'Unavailable here')}
              onClick={() => runtime?.selectAuthoredPiece(entry.id, false)}
            >
              <AuthoredPieceThumbnail descriptorId={entry.id} />
              <b>{displayLabel}</b>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
